import { defineStore } from "pinia";
import type { Child } from "~/types/child";
import { useAuthStore } from "./auth";

interface ChildResponse {
  data: Child[];
  meta: {
    total: number;
    lastPage: number;
    currentPage: number;
  };
}

export const useChildStore = defineStore("child", () => {
  const toast = useToast();
  const authStore = useAuthStore();
  const familyGroupsStore = useGroupStore();
  const children = ref<Child[]>([]);
  // Children belonging to the currently selected family group. Mirrors
  // `residentStore.activeResidents` so pickers can list the right subset
  // instead of every child in the organisation.
  const activeChildren = useState<Child[]>("child.activeChildren", () => []);
  const childrenLoading = ref(false);
  const childrenError = ref<string | null>(null);

  const needRefresh = useState<boolean>("child.needRefresh", () => false);
  const findById = (array: { id: number }[], id: number) => array.find((item) => item.id === id);

  // Watch for current group changes and fetch group-scoped children
  watch(
    () => familyGroupsStore.currentSelectedGroup,
    (selectedGroup) => {
      if (selectedGroup && selectedGroup.id && import.meta.client) {
        fetchActiveChildren(selectedGroup.id);
      }
    },
    { immediate: true },
  );

  async function getChildren(params: Record<string, unknown> = {}): Promise<ChildResponse> {
    if (authStore.currentOrganisation) {
      try {
        const allChildren = (await authStore.api("GET", `/child/index/`, {
          queryString: params,
        })) as ChildResponse;

        if (allChildren && allChildren.data) {
          allChildren.data.forEach((child: Child) => {
            child.birth = child.birth.substring(0, 10);
            child.familyGroup = null;

            if (child.family_group_id) {
              const famGroup = findById(familyGroupsStore.groups, child.family_group_id);
              child.familyGroup = famGroup ? famGroup : null;
            }
          });
        }
        return allChildren;
      } catch (error) {
        console.error("Error fetching children data:", error);
        return { data: [], meta: { total: 0, lastPage: 0, currentPage: 1 } };
      }
    }
    return { data: [], meta: { total: 0, lastPage: 0, currentPage: 1 } };
  }

  async function getChildProfile(id: number): Promise<Child | null> {
    if (authStore.currentOrganisation) {
      try {
        const result = (await authStore.api("GET", `/child/show/${id}`)) as Child;
        return result;
      } catch (error) {
        console.error("Error fetching child data:", error);
        return null;
      }
    }
    return null;
  }

  async function createChild(data: Partial<Child>): Promise<Child | null> {
    if (authStore.currentOrganisation) {
      try {
        const result = (await authStore.api("POST", `/child/create/`, data)) as Child;
        needRefresh.value = true;
        return result;
      } catch (error) {
        console.error("Error creating child:", error);
        throw error;
      }
    }
    return null;
  }

  async function updateChild(data: Partial<Child>): Promise<Child | null> {
    if (authStore.currentOrganisation) {
      try {
        const result = (await authStore.api("PUT", `/child/update/`, data)) as Child;
        needRefresh.value = true;
        return result;
      } catch (error) {
        console.error("Error updating child:", error);
        throw error;
      }
    }
    return null;
  }

  async function deleteChild(childId: number): Promise<boolean> {
    if (authStore.currentOrganisation) {
      try {
        await authStore.api("DELETE", `/child/delete/`, {
          organisation_id: authStore.currentOrganisation,
          child_id: childId,
        });
        needRefresh.value = true;
        toast.add({
          title: "Success",
          description: "Child deleted successfully.",
          icon: "i-heroicons-exclamation-triangle",
          color: "success",
          duration: 5000,
        });
        return true;
      } catch (error) {
        console.error("Error deleting child:", error);
        throw error;
      }
    }
    return false;
  }

  async function getChildrenCount(): Promise<number> {
    if (authStore.currentOrganisation) {
      try {
        const result = (await authStore.api("GET", `/child/count/`)) as number;
        return result;
      } catch (error) {
        console.error("Error fetching children count:", error);
        return 0;
      }
    }
    return 0;
  }

  /**
   * Extract child data from an uploaded document.
   * Returns a raw fetch Response so the caller can consume the NDJSON stream.
   */
  async function extractFromDocument(file: File, signal?: AbortSignal): Promise<Response> {
    const formData = new FormData();
    formData.append("document", file);

    return authStore.api(
      "POST",
      "/child/extract-from-document",
      formData,
      {},
      { stream: true, signal },
    ) as Promise<Response>;
  }

  // Fetch children belonging to a specific family group. Mirrors
  // `fetchActiveResidents` in the resident store; uses the existing
  // `/child/index/` endpoint (which is already scoped by family_group_id).
  async function fetchActiveChildren(groupId: number) {
    try {
      try {
        const nuxtApp = useNuxtApp();
        if (!nuxtApp) {
          console.log("Nuxt app not ready, skipping fetchActiveChildren");
          return;
        }
      } catch (error) {
        console.log("Nuxt app not ready, skipping fetchActiveChildren", error);
        return;
      }

      childrenLoading.value = true;
      childrenError.value = null;

      if (!authStore || typeof authStore.api !== "function") {
        console.log(
          "Auth store not ready or api method not available, skipping fetchActiveChildren",
        );
        childrenLoading.value = false;
        return;
      }

      const response = (await authStore.api("GET", "/child/index/", {
        queryString: { family_group_id: groupId, limit: 100 },
      })) as ChildResponse;

      if (response && Array.isArray(response.data)) {
        activeChildren.value = response.data;
      } else {
        activeChildren.value = [];
      }
    } catch (err: any) {
      console.error("Error fetching active children:", err);
      childrenError.value = err.message || "Failed to load children";
      activeChildren.value = [];
    } finally {
      childrenLoading.value = false;
    }
  }

  return {
    children,
    activeChildren,
    childrenLoading,
    childrenError,
    needRefresh,
    getChildren,
    getChildProfile,
    createChild,
    updateChild,
    deleteChild,
    getChildrenCount,
    extractFromDocument,
    fetchActiveChildren,
  };
});
